/**
 * One live session as the host's studio holds it, for the computer studio and the Season one alike: the meeting, the
 * room's chat on its socket, whether the live has started or ended, going live, ending, and the guards that keep a live
 * from being left by accident.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { type ChatLine, type ChatToRoom, LIVE_CLOSE, type ReactionCounts, type ShareRule } from "../../shared/live-protocol";
import type { LiveEndResponse, LiveStudio, LiveTopupNotice, StudioLive } from "../../shared/types";
import { pathWithoutWorkspace } from "../../shared/workspace-path";
import { api } from "../lib/api";
import { appPath } from "../lib/workspace";
import { chatReducer, EMPTY_CHAT, openRoomChat, type RoomChat, type RoomChatSocket } from "./room/chat";
import { checkClock } from "./room/clock";
import { roomSocketUrl } from "./room/url";
import { type Meeting, type MeetingState } from "./meeting/meeting";
import { useMeeting } from "./meeting/useMeeting";
import { directPath } from "../talk/places";

/** A second's tick, for the clocks on the page. */
export function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

/**
 * While the host is live, ask once a minute whether live hours ran out and 100 more were added
 * (`GET /billing/live-notice`), so only the host hears of it and the live never stops. A note the host closed is not
 * shown again.
 */
export function useLiveTopupNotice(wentLiveAt: number | null): [LiveTopupNotice | null, () => void] {
  const [notice, setNotice] = useState<LiveTopupNotice | null>(null);
  const seen = useRef(0);
  useEffect(() => {
    if (wentLiveAt === null) return;
    let alive = true;
    const ask = () =>
      api.billing
        .liveNotice(Math.max(wentLiveAt, seen.current))
        .then((res) => {
          if (alive && res.notice && res.notice.at > seen.current) setNotice(res.notice);
        })
        .catch(() => {});
    void ask();
    const timer = window.setInterval(() => void ask(), 60_000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [wentLiveAt]);
  const dismiss = useCallback(() => {
    setNotice((was) => {
      if (was) seen.current = was.at;
      return null;
    });
  }, []);
  return [notice, dismiss];
}

export interface StudioSession {
  now: number;
  live: StudioLive;
  isLive: boolean;
  ended: boolean;
  meeting: Meeting;
  state: MeetingState;
  chat: RoomChat;
  /** Seconds since the host went live, on Cloudflare's clock; zero before. */
  liveSec: number;
  /** The door's way in: starts the live if it has not started, then joins the meeting. */
  goIn: () => Promise<void>;
  /** Ends the live for everyone and leaves the meeting. Throws the API's words when it did not end. */
  end: () => Promise<LiveEndResponse>;
  /** Where the host tried to go while live, until they say whether to leave. */
  leaving: string | null;
  setLeaving: (to: string | null) => void;
  topupNotice: LiveTopupNotice | null;
  closeTopupNotice: () => void;
  sayToRoom: (body: string) => boolean;
  send: (msg: ChatToRoom) => boolean;
  pin: (id: string | null) => void;
  hide: (id: string) => void;
  shareTools: { rule: (rule: ShareRule) => void; answer: (registrantId: string, yes: boolean) => void };
  replyPrivately: (line: ChatLine) => Promise<void>;
}

/** `showToast` says a short line to the host and must stay the same function between draws. */
export function useStudioSession(
  info: LiveStudio,
  showToast: (words: string) => void,
  onReacted?: (counts: ReactionCounts) => void,
): StudioSession {
  const { webinarId, sessionId } = info;
  const now = useNow();
  const [live, setLive] = useState<StudioLive>(info.live);
  const [chat, hear] = useReducer(chatReducer, EMPTY_CHAT);
  const chatSocket = useRef<RoomChatSocket | null>(null);
  const [leaving, setLeaving] = useState<string | null>(null);
  const isLive = live.state === "live";
  const reacted = useRef(onReacted);
  reacted.current = onReacted;

  const { meeting, state } = useMeeting(true, () => api.studio.join(webinarId, sessionId));
  const [topupNotice, closeTopupNotice] = useLiveTopupNotice(isLive ? live.wentLiveAt : null);

  // The API stamps when the live started on Cloudflare's clock, so the live clock is read against that rather than
  // this laptop's.
  const [clockOff, setClockOff] = useState(0);
  useEffect(() => {
    let alive = true;
    void checkClock(info.liveOrigin).then((check) => {
      if (alive && check) setClockOff(check.off);
    });
    return () => {
      alive = false;
    };
  }, [info.liveOrigin]);

  // The room's chat, as the host.
  useEffect(() => {
    let first = true;
    const url = async () => {
      const ticket = first ? info.ticket : (await api.studio.ticket(webinarId)).ticket;
      first = false;
      return roomSocketUrl(info.liveOrigin, sessionId, "chat", ticket);
    };
    // Reactions float over the table and feed the line about the room; they stay out of the chat.
    const socket = openRoomChat(url, (event) => {
      if (event.type !== "reacted") {
        hear(event);
        return;
      }
      reacted.current?.(event.counts);
    });
    chatSocket.current = socket;
    // Why pagehide: it is the last event a closing tab or a page left for another address is sure to get (MDN
    // Window pagehide event, checked 26 Oct 2026: https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event),
    // and a close code of our own (3000 to 4999) is delivered to the room as sent (MDN WebSocket.close, same day).
    // A page kept in the back-forward cache stays alive, so the socket still rejoins when it comes back.
    // Why not on leaving the studio inside the app: a host who looks at another page mid-live has not left it, so
    // the room gives them the longer wait of a dropped connection.
    const goodbye = () => socket.sayGoodbye(LIVE_CLOSE.hostLeft);
    window.addEventListener("pagehide", goodbye);
    return () => {
      window.removeEventListener("pagehide", goodbye);
      chatSocket.current = null;
      socket.close();
    };
  }, [info, webinarId, sessionId]);

  // While live: the screen stays awake, and leaving asks first.
  // Wake lock, checked 18 Sep 2026: https://developer.mozilla.org/en-US/docs/Web/API/Screen_Wake_Lock_API
  // (released when the page is hidden, so it is asked for again on return).
  useEffect(() => {
    if (!isLive || !("wakeLock" in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let done = false;
    const take = () => {
      if (document.visibilityState !== "visible") return;
      navigator.wakeLock
        .request("screen")
        .then((l) => {
          if (done) void l.release();
          else lock = l;
        })
        .catch(() => {});
    };
    take();
    document.addEventListener("visibilitychange", take);
    return () => {
      done = true;
      document.removeEventListener("visibilitychange", take);
      void lock?.release().catch(() => {});
    };
  }, [isLive]);

  // beforeunload, checked 18 Sep 2026: https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
  // The app's own links are caught first, so leaving from the page asks in the app's words.
  useEffect(() => {
    if (!isLive) return;
    const unload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = true;
    };
    const click = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!link || link.target === "_blank") return;
      const to = new URL(link.href);
      if (to.origin !== window.location.origin) return;
      e.preventDefault();
      e.stopPropagation();
      setLeaving(pathWithoutWorkspace(to.pathname) + to.search);
    };
    window.addEventListener("beforeunload", unload);
    document.addEventListener("click", click, true);
    return () => {
      window.removeEventListener("beforeunload", unload);
      document.removeEventListener("click", click, true);
    };
  }, [isLive]);

  // The door keeps its button locked until this settles.
  const goIn = useCallback((): Promise<void> => {
    if (live.state === "live") return meeting.enter();
    return api.studio
      .start(webinarId, sessionId)
      .then((started) => {
        setLive(started);
        return meeting.enter();
      })
      .catch((err: unknown) => showToast(err instanceof Error ? err.message : "The live did not start. Try again."));
  }, [live.state, meeting, webinarId, sessionId, showToast]);

  const end = useCallback(async () => {
    const ended = await api.studio.end(webinarId, sessionId);
    await meeting.leave();
    setLive(ended.live);
    return ended;
  }, [meeting, webinarId, sessionId]);

  // Why stable callbacks: the table redraws on every change in the room, and the chat should not redraw with it.
  const sayToRoom = useCallback((body: string) => chatSocket.current?.send({ type: "say", body }) ?? false, []);
  const send = useCallback((msg: ChatToRoom) => chatSocket.current?.send(msg) ?? false, []);
  const pin = useCallback((id: string | null) => void chatSocket.current?.send({ type: "pin", id }), []);
  const hide = useCallback((id: string) => void chatSocket.current?.send({ type: "hide", id }), []);
  const shareTools = useMemo(
    () => ({
      rule: (rule: ShareRule) => void chatSocket.current?.send({ type: "share-rule", rule }),
      answer: (registrantId: string, yes: boolean) =>
        void chatSocket.current?.send({ type: "share-answer", registrantId, yes }),
    }),
    [],
  );
  const replyPrivately = useCallback(
    async (line: ChatLine) => {
      const registrantId = line.registrantId;
      if (!registrantId) return;
      try {
        const { contactId } = await api.talk.directFor(registrantId);
        window.open(appPath(directPath(contactId)), "_blank", "noopener");
        showToast(`Opened a private chat with ${line.name.split(" ")[0]}`);
      } catch (err) {
        showToast(err instanceof Error ? err.message : "That chat did not open. Try again.");
      }
    },
    [showToast],
  );

  const liveSec = isLive && live.wentLiveAt ? (now + clockOff) / 1000 - live.wentLiveAt : 0;
  const ended = chat.state === "ended" || live.state === "ended";

  return {
    now,
    live,
    isLive,
    ended,
    meeting,
    state,
    chat,
    liveSec,
    goIn,
    end,
    leaving,
    setLeaving,
    topupNotice,
    closeTopupNotice,
    sayToRoom,
    send,
    pin,
    hide,
    shareTools,
    replyPrivately,
  };
}
